import type { FunctionComponent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { plural } from "../../../../../locales/i18n";
import styled from "styled-components";
import { GrayColors } from "@smartsuite/types";
import type { CurrentItemPair } from "./get-current-item";

interface CurrentItemCodeBlockProps {
  pairs: CurrentItemPair[];
}

/** Pairs-per-column threshold: above it the block splits into two columns
 * (the mock's 35-key payload reads far better side by side). */
const TWO_COLUMN_THRESHOLD = 12;

/** Minimum panel width for two side-by-side columns (2x320px of content +
 * body padding + panel border). Measured with a ResizeObserver because the
 * run-history pane is user-resizable (a viewport media query would not track
 * it) and styled-components v5 (stylis 3) drops @container rules. Below it a
 * large object renders as ONE column, so the "& + &" separator can never
 * linger on a wrapped second column. */
const TWO_COLUMN_MIN_PANEL_WIDTH = 650;

/** How long the "Copied" confirmation replaces the copy button label. */
const COPY_FEEDBACK_MS = 1600;

// Keys pick up the solution accent (the same variable the canvas uses, with
// the default solution blue as fallback); string values are near-black text,
// numbers stay warm so typed values still stand out.
const SYNTAX = {
  key: "var(--solution-primary-color, #3A86FF)",
  string: "#4d4d4d",
  number: "#cf222e",
  punctuation: GrayColors["G-3"],
} as const;

const StyledPanel = styled.div`
  border: 1px solid ${GrayColors["G-1-5"]};
  border-radius: 8px;
  background: ${GrayColors["G-0"]};
  overflow: hidden;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${GrayColors["G-1-5"]};
`;

const StyledTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${GrayColors["G-9"]};
`;

const StyledChip = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: ${GrayColors["G-6"]};
  background: ${GrayColors["G-1"]};
  border-radius: 4px;
  padding: 2px 6px;
`;

const StyledKeyCount = styled.span`
  font-size: 12px;
  color: ${GrayColors["G-4"]};
  font-variant-numeric: tabular-nums;
`;

const StyledCopyButton = styled.button`
  margin-left: auto;
  border: 1px solid ${GrayColors["G-1-5"]};
  border-radius: 6px;
  background: #fff;
  color: ${GrayColors["G-9"]};
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  cursor: pointer;
  /* Widest label ("Copy all as JSON") sets the box, so swapping to the
     shorter "Copied" causes no layout shift. */
  min-width: 122px;
  text-align: center;
  transition: border-color 120ms ease, color 120ms ease;

  @media (hover: hover) {
    &:hover {
      border-color: ${GrayColors["G-2"]};
    }
  }

  &:focus-visible {
    outline: 2px solid ${SYNTAX.key};
    outline-offset: 1px;
  }
`;

const StyledBody = styled.div`
  display: flex;
  padding: 10px 4px;
  overflow-x: auto;
`;

// Two columns render only when the measured panel width fits them side by
// side (see TWO_COLUMN_MIN_PANEL_WIDTH), so the sibling separator is always a
// real vertical divider -- never a stray left border on a stacked column.
const StyledColumn = styled.div`
  flex: 1;

  & + & {
    border-left: 1px solid ${GrayColors["G-1"]};
  }
`;

const StyledLine = styled.div`
  display: flex;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 20px;
  white-space: pre;
`;

const StyledLineNumber = styled.span`
  flex: none;
  width: 34px;
  padding-right: 10px;
  text-align: right;
  color: ${GrayColors["G-3"]};
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

const StyledKey = styled.span`
  color: ${SYNTAX.key};
`;

const StyledPunctuation = styled.span`
  color: ${SYNTAX.punctuation};
`;

const StyledValue = styled.span<{ $kind: CurrentItemPair["kind"] }>`
  color: ${(props) =>
    props.$kind === "number" ? SYNTAX.number : SYNTAX.string};
`;

interface CodeLine {
  number: number;
  content: JSX.Element;
}

/** The pair's JSON value token: numbers raw, everything else quoted. */
const valueToken = (pair: CurrentItemPair): string =>
  pair.kind === "number" ? pair.value : JSON.stringify(pair.value);

const toJson = (pairs: CurrentItemPair[]): string =>
  `{\n${pairs
    .map((pair) => `  ${JSON.stringify(pair.key)}: ${valueToken(pair)}`)
    .join(",\n")}\n}`;

/**
 * The code-block view of a structured (webhook object) Current Item: header
 * with an application/json chip, a key count and a copy button; body with
 * line numbers and syntax colors, split into two columns for large objects
 * (they wrap back into one on a narrow panel). Tokens are rendered from the
 * structured pairs directly -- no parser or highlight library.
 */
export const CurrentItemCodeBlock: FunctionComponent<
  CurrentItemCodeBlockProps
> = ({ pairs }) => {
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();
  // Default wide: jsdom's stubbed ResizeObserver never fires, and on a narrow
  // real panel the observer corrects the very first frame.
  const [isWide, setIsWide] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  useEffect(() => {
    const panel = panelRef.current;
    if (panel === null) {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setIsWide(width >= TWO_COLUMN_MIN_PANEL_WIDTH);
    });
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  const handleCopy = useCallback(() => {
    void navigator.clipboard?.writeText(toJson(pairs));
    setCopied(true);
    clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }, [pairs]);

  const lines: CodeLine[] = [
    { number: 1, content: <StyledPunctuation>{"{"}</StyledPunctuation> },
    ...pairs.map((pair, index) => ({
      number: index + 2,
      content: (
        <>
          {"  "}
          <StyledKey>{JSON.stringify(pair.key)}</StyledKey>
          <StyledPunctuation>: </StyledPunctuation>
          <StyledValue $kind={pair.kind}>{valueToken(pair)}</StyledValue>
          {index < pairs.length - 1 && <StyledPunctuation>,</StyledPunctuation>}
        </>
      ),
    })),
    {
      number: pairs.length + 2,
      content: <StyledPunctuation>{"}"}</StyledPunctuation>,
    },
  ];

  const columns: CodeLine[][] =
    pairs.length > TWO_COLUMN_THRESHOLD && isWide
      ? [
          lines.slice(0, Math.ceil(lines.length / 2)),
          lines.slice(Math.ceil(lines.length / 2)),
        ]
      : [lines];

  return (
    <StyledPanel ref={panelRef}>
      <StyledHeader>
        <StyledTitle>{t("job-list.current-item")}</StyledTitle>
        <StyledChip>application/json</StyledChip>
        <StyledKeyCount>
          {t(plural("job-list.key-count", pairs.length), {
            count: pairs.length,
          })}
        </StyledKeyCount>
        <StyledCopyButton type="button" onClick={handleCopy}>
          {copied ? t("job-list.copied") : t("job-list.copy-all-as-json")}
        </StyledCopyButton>
      </StyledHeader>
      <StyledBody>
        {columns.map((column, columnIndex) => (
          <StyledColumn key={`column-${columnIndex}`} data-testid="code-column">
            {column.map((line) => (
              <StyledLine key={line.number}>
                <StyledLineNumber aria-hidden="true">
                  {line.number}
                </StyledLineNumber>
                {line.content}
              </StyledLine>
            ))}
          </StyledColumn>
        ))}
      </StyledBody>
    </StyledPanel>
  );
};
